import { AssetType, JobType, ScheduleTimeBasedPattern, ScheduleType } from '@/enum'
import type { IJobForm } from '@/interfaces'
import { JobAssociatedAssetInfo, JobUpdate } from '@/models'
import { default as IJobRequestMap, default as JobRequestMap } from '@/models/jobs/JobRequestMap'
import { DATE_FORMAT, DEFAULT_JOB_START_TIME } from '@/utils/constants'
import { getDate, getEndOfDay, isCurrentDate, isDateInFuture } from '@/utils/helpers'
import moment from 'moment'
import { cloneDeep } from 'lodash'
import { ValueBasedTriggerType } from '@/enum/ValueBasedTriggerType'

class JobRequestMapper {
  jobRequestMap: IJobRequestMap
  jobData: any
  jobScheduleData: any

  constructor() {
    this.jobRequestMap = new JobRequestMap()
  }

  private handleJobScheduleOptions(job: IJobForm | any, scheduleOptionsKey: string) {
    job[scheduleOptionsKey].timeBasedTrigger.startFrom = job.jobStartDate as string

    // if job is not maintenance or inspection type
    if (job.type !== JobType.MAINTENANCE && job.type !== JobType.INSPECTION) {
      delete job[scheduleOptionsKey].machineHoursIncrementTrigger
      delete job[scheduleOptionsKey].odometerReadingIncrementTrigger
    }

    if (job[scheduleOptionsKey].timeBasedTrigger?.pattern === ScheduleTimeBasedPattern.YEARS) {
      if (job[scheduleOptionsKey].timeBasedTrigger?.dates?.length > 0) {
        job[scheduleOptionsKey].timeBasedTrigger.dates = job[
          scheduleOptionsKey
        ].timeBasedTrigger.dates.map((date: string) => getDate(date, DATE_FORMAT))
      }
    }

    if (job[scheduleOptionsKey].timeBasedTrigger.dates == '')
      job[scheduleOptionsKey].timeBasedTrigger.dates = []

    if (job[scheduleOptionsKey].timeBasedTrigger.repeatAfterEvery)
      job[scheduleOptionsKey].timeBasedTrigger.repeatAfterEvery = parseInt(
        job[scheduleOptionsKey].timeBasedTrigger.repeatAfterEvery
      )

    if (job[scheduleOptionsKey].numberOfOccurrences)
      job[scheduleOptionsKey].numberOfOccurrences = parseInt(
        job[scheduleOptionsKey].numberOfOccurrences
      )

    if (!job.isOdometerBasedSelected) job[scheduleOptionsKey].odometerReadingIncrementTrigger = null

    if (!job.isMachineHoursBasedSelected)
      job[scheduleOptionsKey].machineHoursIncrementTrigger = null

    if (!job.isTimeBasedSelected && JobType.GENERAL !== job.type && JobType.WORK_ORDER !== job.type)
      job[scheduleOptionsKey].timeBasedTrigger = null

    if (
      job[scheduleOptionsKey].timeBasedTrigger?.pattern == null ||
      job[scheduleOptionsKey].timeBasedTrigger?.pattern == undefined
    ) {
      job[scheduleOptionsKey].timeBasedTrigger = null
    }

    return job[scheduleOptionsKey]
  }

  private toJobUpdateFromJobForm(job: any): IJobRequestMap {
    this.jobRequestMap.jobRequestData = new JobUpdate(job)
    return this.jobRequestMap
  }

  private toJobAsset(job: any) {
    let asset = null

    if (job?.asset?.vehicleId || job?.asset?.equipmentId || job?.asset?.machineId) {
      const id = job?.asset?.vehicleId || job?.asset?.equipmentId || job?.asset?.machineId
      const assetType = job?.asset?.assetType
      asset = { id, type: assetType }
    } else if (job?.asset?.id) {
      asset = { id: job?.asset?.id, type: job?.asset?.type }
    }

    return asset
  }

  private toJobAssetInfo(jobSchedule: any) {
    let assetInfo

    if (jobSchedule?.asset?.id) {
      if (jobSchedule?.asset?.type === AssetType.VEHICLE) {
        assetInfo = new JobAssociatedAssetInfo({
          machineHours: jobSchedule?.asset?.vehicle?.machineHours,
          odometerReading: jobSchedule?.asset?.vehicle?.odometerReading
        })
      }

      if (jobSchedule?.asset?.type === AssetType.EQUIPMENT) {
        assetInfo = new JobAssociatedAssetInfo({
          machineHours: jobSchedule?.asset?.equipment?.machineHours
        })
      }

      if (jobSchedule?.asset?.type === AssetType.MACHINE) {
        assetInfo = new JobAssociatedAssetInfo({
          machineHours: jobSchedule?.asset?.machine?.machineHours,
          odometerReading: jobSchedule?.asset?.machine?.odometerReading
        })
      }
    }

    return assetInfo
  }

  private toMaintenanceTypesUpdate(job: any) {
    if (job?.maintenanceTypes?.length == 0) return []

    return (
      job.maintenanceTypes?.map((maintenanceType: any) => {
        return {
          id: maintenanceType.typeId || maintenanceType.id,
          workHours: maintenanceType.workHours
        }
      }) || []
    )
  }

  private toJobScheduleUpdateFromJobScheduleForm(job: any): IJobRequestMap {
    this.jobRequestMap.jobRequestData = {
      title: job.title,
      description: job.description,
      jobType: job.jobType,
      priority: job.priority,
      asset: this.toJobAsset(job),
      assetInfo: this.toJobAssetInfo(job),
      maintenanceTypes: this.toMaintenanceTypesUpdate(job),
      options: this.handleJobScheduleOptions({ ...job, type: job.jobType }, 'options'),
      assignedEmployeeIds: Array.isArray(job.assignedEmployeeIds) 
                          ? job.assignedEmployeeIds 
                          : (
                              job.assignedEmployeeIds 
                              ? [job.assignedEmployeeIds] 
                              : []
                            ),
      workOrderVendorId: job.workOrderVendorId,
      taskSheets: job.taskSheets,
      documents: job.documents
    }

    return this.jobRequestMap
  }

  public toJobUpdate(jobOriginal: any) {
    const job = cloneDeep(jobOriginal)
    this.jobRequestMap.shouldScheduleJob = false
    return this.toJobUpdateFromJobForm(job)
  }

  public toJobSchedule(jobOriginal: any) {
    const job = cloneDeep(jobOriginal)
    this.jobRequestMap.shouldScheduleJob = true
    return this.toJobScheduleUpdateFromJobScheduleForm(job)
  }
}

export default JobRequestMapper
